import "bootstrap/dist/css/bootstrap.min.css";
import './App.css';
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
    BasicStorage,
    ChatMessage,
    ChatProvider,
    Conversation,
    ConversationId,
    ConversationRole,
    IStorage,
    MessageContentType,
    Participant,
    Presence,
    TypingUsersList,
    UpdateState,
    User,
    UserStatus
} from "./@chatscope/use-chat/src";

import { ChatService } from "./ChatService";
import { Chat } from "./components/Chat";
import { nanoid } from "nanoid";
import { Col, Container, Row, Navbar } from "react-bootstrap";
import { AutoDraft } from "./@chatscope/use-chat/src/enums/AutoDraft";
import { Footer } from "./components/Footer";
import { PublicClientApplication } from "@azure/msal-browser";
import { sign } from "crypto";
import React, { useState, useEffect } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useMsalAuthentication } from "@azure/msal-react";
import { InteractionType, InteractionRequiredAuthError } from '@azure/msal-browser';
import { get } from "http";
import { getUserData } from "./utils/peopleSearch";
import { FsnHeader } from '@fusion/components/Header';
import { FsnThemeProvider } from '@fusion/components/theme';
import { FsnUser } from '@fusion/components/common';
import { FsnUserInfo } from '@fusion/components/UserInfo';
import { Button, MenuItem, Tooltip } from "@mui/material";
import { FsnMenuHeaderAction } from '@fusion/components/MenuHeaderAction';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InstallDesktopIcon from '@mui/icons-material/InstallDesktop';
import { getToken } from "./getAccessToken";
import { reactPlugin, appInsights } from "./utils/appInsights";
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import Menu from "./components/Menu";
appInsights.loadAppInsights();



// sendMessage and addMessage methods can automatically generate id for messages and groups.
// This allows you to omit doing this manually, but you need to provide a message generator
// The message id generator is a function that receives message and returns id for this message
// The group id generator is a function that returns string
const messageIdGenerator = (message: ChatMessage<MessageContentType>) => nanoid();
const groupIdGenerator = () => nanoid();

const userStorage = new BasicStorage({ groupIdGenerator, messageIdGenerator });



export function App() {
    const { login, result, error } = useMsalAuthentication(InteractionType.Redirect);

    const { instance, accounts, inProgress } = useMsal();

    // Initialize currentUser as a state variable
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    // Create serviceFactory
    const serviceFactory = (storage: IStorage, updateState: UpdateState) => {
        return new ChatService(storage, updateState, instance);
    };

    useEffect(() => {
        async function fetchUser() {

            let currentUserData = await getUserData(instance, accounts[0]?.username as string);
            setCurrentUser(currentUserData);
        }
        // Check if authentication is not in progress and a user is logged in
        if (inProgress === "none" && accounts.length > 0) {
            // Call the function
            fetchUser();
        }
    }, [inProgress, accounts]);

    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

    useEffect(() => {
        if (appInstalled()) {
            console.log('Ping app is installed.');
        } else {
            console.log('Ping app is not installed.');
            window.addEventListener('beforeinstallprompt', (e: any) => {
                // Prevent Chrome 67 and earlier from automatically showing the prompt
                e.preventDefault();
                // Stash the event so it can be triggered later.

                setDeferredPrompt(e);
            });
        }
    }, []);

    const appInstalled = () => {
        return window.matchMedia('(display-mode: standalone)').matches;
    };

    const handleInstallClick = () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choiceResult: any) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('User accepted the install prompt');
                } else {
                    console.log('User dismissed the install prompt');
                }
                setDeferredPrompt(null);
            });
        } else {
            console.log('deferredPrompt is null');
        }
    };


    const [settingsMenu, setSettingsMenu] = useState(false);

    const handleMenuClick = () => {
        setSettingsMenu((prevState) => !prevState); 
        };

    const demoUser: FsnUser = {
        displayName: accounts[0]?.name,
        email: accounts[0]?.username,
        gmin: accounts[0]?.idTokenClaims?.gmuniqueidentifier as string
    };

    return (
        <AppInsightsContext.Provider value={reactPlugin}>
            <div className="h-100 d-flex flex-column overflow-hidden">
                <FsnThemeProvider themeSetting="light">
                    <FsnHeader menuVisible={false} position="fixed" title="Ping!">
                        { (deferredPrompt !== null) && 
                            <Tooltip title="Install Ping! to Desktop">
                                <Button variant="contained" startIcon={<InstallDesktopIcon />} onClick={handleInstallClick}>Install</Button>
                            </Tooltip>
                            }
                            <Button startIcon={<SettingsIcon color="secondary" sx={{ fontSize: 26 }} />} onClick={handleMenuClick} ></Button>
                        <FsnUserInfo user={demoUser} />
                    </FsnHeader>
                </FsnThemeProvider>
                <React.Fragment>
                    <AuthenticatedTemplate>
                        <Container fluid className="p-4 flex-grow-1 position-relative overflow-hidden">

                            <ChatProvider serviceFactory={serviceFactory} storage={userStorage} config={{
                                typingThrottleTime: 250,
                                typingDebounceTime: 900,
                                debounceTyping: true,
                                autoDraft: AutoDraft.Save | AutoDraft.Restore
                            }}>
                                {/* Only render Chat if currentUser is set */}
                                {currentUser && <Chat user={currentUser} />}
                                {settingsMenu && <Menu />}

                                <footer style={{ fontSize: "12px", color: "dimgray", marginTop: "2px" }}>
                                    Ping! is NOT approved for Litigation Hold or Business Record content. See full AUP <a href="https://gmweb.gm.com/sites/service/Pages/PingAup.aspx" target="blank">here.</a>
                                </footer>

                            </ChatProvider>

                        </Container>
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                        <p>No users are signed in!</p>
                    </UnauthenticatedTemplate>
                </React.Fragment>

            </div>
        </AppInsightsContext.Provider >
    );
}

export default App;
